import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'react-feather';
import TableBuilder, { ApiRequest, ToastBuilder, ButtonBuilder, VerticalButtonGroup, ModalLinkBuilder } from '../../../../../../GlobalComponents/Builders';
import { Row, Col, CardBody, CardHeader, Card, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { H4 } from '../../../../../../AbstractElements';
import { showRelativeTime, parseJSONSafe, jsonPre, useIsMobile } from '../../../../../../GlobalComponents/Helpers';
import ActivityLogViewer from '../../../Jobs/Job/DMT/components/shared/ActivityLogViewer';

const DMTSyncErrors = () => {

    document.title = 'DMT Sync Errors';

    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [allErrors, setAllErrors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('active');

    const tabs = [
        { key: 'active', label: 'Active' },
        { key: 'notified', label: 'Notified' },
        { key: 'resolved', label: 'Resolved' },
        { key: 'dismissed', label: 'Dismissed' },
        { key: 'all', label: 'All' },
    ];

    const filteredErrors = useMemo(() => {
        if (activeTab === 'all') return allErrors;
        if (activeTab === 'active') return allErrors.filter(e => !e.resolved_at && !e.is_dismissed);
        if (activeTab === 'notified') return allErrors.filter(e => !e.resolved_at && e.is_notified && !e.is_dismissed);
        if (activeTab === 'resolved') return allErrors.filter(e => e.resolved_at && !e.is_dismissed);
        if (activeTab === 'dismissed') return allErrors.filter(e => e.is_dismissed);
        return allErrors;
    }, [allErrors, activeTab]);

    const fetchErrors = () => {
        setLoading(true);
        console.log('[DMTSyncErrors] Fetching all errors');

        ApiRequest('GET', '/dmt/errors', { queryParams: { table: 'dmt_sync_error_tracker' } })
            .then((response) => {
                console.log('[DMTSyncErrors] Fetch response:', response.data);
                setAllErrors(response?.data || []);
                setLoading(false);
            })
            .catch((err) => {
                console.error('[DMTSyncErrors] Fetch failed:', err);
                setLoading(false);
                ToastBuilder('error', 'Failed to load sync errors');
            });
    };

    useEffect(() => {
        fetchErrors();
    }, []);

    const handleDismiss = (errorId) => {
        console.log('[DMTSyncErrors] Dismissing error:', errorId);
        ApiRequest('PATCH', '/dmt/errors/{errorId}', { pathParams: { errorId }, body: [{ op: 'replace', path: 'is_dismissed', value: 1 }], queryParams: { table: 'dmt_sync_error_tracker' } })
            .then(() => {
                ToastBuilder('success', 'Error dismissed');
                fetchErrors();
            })
            .catch((err) => {
                console.error('[DMTSyncErrors] Dismiss failed:', err);
                ToastBuilder('error', 'Failed to dismiss error');
            });
    };

    const handleNotify = () => {
        ApiRequest('POST', '/dmt/errors/notify')
            .then((response) => {
                const result = parseJSONSafe(response?.data?.records || response?.data);
                if (result?.success && result?.notified > 0) {
                    ToastBuilder('success', `Notified ${result.notified} error(s)`);
                    fetchErrors();
                } else if (result?.success) {
                    ToastBuilder('success', 'No errors above threshold');
                } else {
                    ToastBuilder('error', result?.message || 'Notification failed');
                }
            })
            .catch(() => {
                ToastBuilder('error', 'Failed to send notifications');
            });
    };

    const handleRenotifyAll = () => {
        ApiRequest('POST', '/dmt/errors/notify', { body: { renotify: true } })
            .then((response) => {
                const result = parseJSONSafe(response?.data?.records || response?.data);
                if (result?.success && result?.notified > 0) {
                    ToastBuilder('success', `Re-notified ${result.notified} unresolved error(s)`);
                    fetchErrors();
                } else if (result?.success) {
                    ToastBuilder('success', 'No unresolved errors to notify');
                } else {
                    ToastBuilder('error', result?.message || 'Re-notification failed');
                }
            })
            .catch(() => {
                ToastBuilder('error', 'Failed to send re-notifications');
            });
    };

    // Retry a failed record: same queue-resource payload the Progress page sends,
    // with fetchUpdates + ignoreTerminalState so a terminal-state record is reprocessed
    // against freshly fetched source data. dryRun: false = live run.
    const handleRetry = (row) => {
        ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId: row.job_id, actionName: 'queue-resource' },
            body: {
                sourceResourceId: row.resource_id,
                formData: {
                    resourceId: row.resource_id,
                    fetchUpdates: true,
                    ignoreTerminalState: true,
                    dryRun: false,
                    overrideLock: false,
                    isolateLogs: false,
                    valuesToQueue: [String(row.source_rec_id)],
                    limit: 1,
                },
            },
        })
            .then((res) => {
                if (res.success) {
                    ToastBuilder('success', `Retry queued for record ${row.source_rec_id}`);
                } else {
                    ToastBuilder('error', res.error || 'Failed to queue retry');
                }
            })
            .catch(() => {
                ToastBuilder('error', 'Failed to queue retry');
            });
    };

    const getStatusBadge = (row) => {
        if (row.is_dismissed) return <span className="badge bg-secondary">Dismissed</span>;
        if (row.resolved_at) return <span className="badge bg-success">Resolved</span>;
        if (row.is_notified) return <span className="badge bg-warning text-dark">Notified</span>;
        if (row.error_count >= 3) return <span className="badge bg-danger">Critical</span>;
        return <span className="badge bg-danger" style={{ opacity: 0.7 }}>Active</span>;
    };

    const tabCounts = useMemo(() => ({
        active: allErrors.filter(e => !e.resolved_at && !e.is_dismissed).length,
        notified: allErrors.filter(e => !e.resolved_at && e.is_notified && !e.is_dismissed).length,
        resolved: allErrors.filter(e => e.resolved_at && !e.is_dismissed).length,
        dismissed: allErrors.filter(e => e.is_dismissed).length,
        all: allErrors.length,
    }), [allErrors]);

    const renderTable = (data) => (
        <TableBuilder
            data={data}
            processing={loading}
            pagination
            paginationPerPage={25}
            sortField={'error_count'}
            sortOrderAsc={false}
            subHeader={true}
            columnsToSearch={['job_id', 'resource_id', 'source_rec_id', 'last_error_action']}
            columns={[
                {
                    // Merged Job + Resource: labeled job line on top, resource (id + source→dest names) below.
                    name: 'Job / Resource',
                    selector: (row) => `${row.job_id} ${row.job_name || ''} ${row.resource_id} ${row.source_resource_name || ''}`,
                    sortable: true,
                    center: false,
                    grow: 2,
                    minWidth: '220px',
                    cell: (row) => (
                        <div className='d-flex flex-column' style={{ gap: 4, lineHeight: 1.25, padding: '6px 0', width: '100%' }}>
                            <div className='d-flex align-items-baseline' style={{ gap: 6 }}>
                                <span className='txt-secondary' style={{ fontSize: 10, letterSpacing: 0.3, minWidth: 30 }}>JOB</span>
                                <span style={{ fontWeight: 600 }}>{row.job_id}</span>
                                {row.job_name ? <span className='text-muted' style={{ fontSize: 12 }}>{row.job_name}</span> : null}
                            </div>
                            <div className='d-flex align-items-baseline' style={{ gap: 6 }}>
                                <span className='txt-secondary' style={{ fontSize: 10, letterSpacing: 0.3, minWidth: 30 }}>RES</span>
                                <span style={{ fontWeight: 600 }}>{row.resource_id}</span>
                                {row.source_resource_name ? (
                                    <span className='text-muted' style={{ fontSize: 12 }}>
                                        {row.source_resource_name}{row.destination_resource_name ? ` → ${row.destination_resource_name}` : ''}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    ),
                },
                {
                    name: 'Record',
                    selector: (row) => row.source_rec_id,
                    sortable: true,
                    center: true,
                },
                {
                    // Merged Failures + Seen: failure count on top (red/bold when >=3),
                    // last-seen relative time (with first-seen subtext) below.
                    name: 'Failures',
                    selector: (row) => row.error_count,
                    sortable: true,
                    center: true,
                    sortFunction: (a, b) => a.error_count - b.error_count,
                    cell: (row) => (
                        <div className='d-flex flex-column align-items-center' style={{ gap: 3, lineHeight: 1.2, padding: '4px 0' }}>
                            <span style={{
                                color: row.error_count >= 3 ? '#d32f2f' : '#666',
                                fontWeight: row.error_count >= 3 ? 'bold' : 'normal',
                            }}>
                                {`${row.error_count} ${row.error_count === 1 ? 'failure' : 'failures'}`}
                            </span>
                            <span className='text-muted' style={{ fontSize: 11 }}>
                                {row.last_error_at ? showRelativeTime(row.last_error_at) : '-'}
                            </span>
                            {row.first_error_at && row.first_error_at !== row.last_error_at ? (
                                <span className='text-muted' style={{ fontSize: 10 }}>{`first: ${showRelativeTime(row.first_error_at)}`}</span>
                            ) : null}
                        </div>
                    ),
                },
                {
                    // Merged Status + Action + Details: status badge and error-details icon on
                    // top (side by side), last error action below.
                    name: 'Status',
                    selector: (row) => row.last_error_action || '-',
                    sortable: true,
                    center: true,
                    cell: (row) => {
                        const msg = row.last_error_message;
                        const parsed = msg ? parseJSONSafe(msg) : null;
                        return (
                            <div className='d-flex flex-column align-items-center' style={{ gap: 4, lineHeight: 1.2, padding: '4px 0' }}>
                                <div className='d-flex align-items-center justify-content-center' style={{ gap: 6 }}>
                                    {getStatusBadge(row)}
                                    <ModalLinkBuilder
                                        onSubmit={false}
                                        onCancelLabel='Close'
                                        label={<AlertCircle size={16} className='icon-solid-danger' style={{ cursor: 'pointer', verticalAlign: 'middle' }} />}
                                        title={`Error Details — Record ${row.source_rec_id}`}
                                        size='lg'
                                        body={
                                            <div>
                                                <h6>Error Message</h6>
                                                <div style={{ maxHeight: 500, overflow: 'auto' }}>
                                                    {parsed || msg ? jsonPre(parsed || msg) : <p className='text-muted'>No error message</p>}
                                                </div>
                                                <hr />
                                                <h6>Activity Log</h6>
                                                <ActivityLogViewer
                                                    jobId={row.job_id}
                                                    resourceId={row.resource_id}
                                                    sourceRecId={row.source_rec_id}
                                                />
                                            </div>
                                        }
                                    />
                                </div>
                                <span style={{ fontSize: 12 }}>{row.last_error_action || '-'}</span>
                            </div>
                        );
                    },
                },
                {
                    name: 'Manage',
                    center: true,
                    cell: (row) => {
                        // Each button carries its own onClick directly — VerticalButtonGroup
                        // reads props.onClick off each element, so no key-based inference.
                        const btns = [];

                        if (row.resolved_at && !row.is_dismissed) {
                            btns.push(
                                <button
                                    key="dismiss"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => handleDismiss(row.error_id)}
                                >
                                    Dismiss
                                </button>
                            );
                        }

                        btns.push(
                            <button
                                key="viewjob"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => window.open(`${process.env.PUBLIC_URL}/tools/data-migration/jobs/${row.job_id}`, '_blank')}
                            >
                                View Job
                            </button>
                        );

                        btns.push(
                            <button
                                key="progress"
                                className="btn btn-outline-info btn-sm"
                                onClick={() => window.open(`${process.env.PUBLIC_URL}/tools/data-migration/jobs/${row.job_id}/progress?record_id=${row.source_rec_id}`, '_blank')}
                            >
                                Progress
                            </button>
                        );

                        btns.push(
                            <button
                                key="retry"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleRetry(row)}
                            >
                                Retry
                            </button>
                        );

                        return (
                            <div className="d-flex align-items-center gap-1">
                                <VerticalButtonGroup
                                    label='Actions'
                                    className='btn-primary'
                                    style={isMobile ? { minWidth: 135, paddingLeft: 8, paddingRight: 8 } : {}}
                                    buttons={btns}
                                />
                            </div>
                        );
                    },
                },
            ]}
        />
    );

    return (
        <Row>
            <Col sm="12">
                <Card>
                    <CardHeader className="d-flex justify-content-between align-items-center pb-1">
                        <H4 className="mb-0">Sync Errors</H4>
                        <div className="d-flex gap-2">
                            <ButtonBuilder label="Send Notifications" onClick={handleNotify} color={null} className="btn-primary" size="sm" useSpinner={false} />
                            <ButtonBuilder label="Re-notify All" onClick={handleRenotifyAll} color={null} className="btn-warning" size="sm" useSpinner={false} />
                            <ButtonBuilder label="Refresh" onClick={fetchErrors} color={null} className="btn-secondary" size="sm" useSpinner={false} />
                        </div>
                    </CardHeader>
                    <CardBody>
                        <Nav tabs>
                            {tabs.map((tab) => (
                                <NavItem key={tab.key}>
                                    <NavLink
                                        href="#"
                                        className={activeTab === tab.key ? 'active' : ''}
                                        onClick={() => setActiveTab(tab.key)}
                                    >
                                        <span className="d-inline-flex align-items-center gap-2">
                                            {tab.label}
                                            {tabCounts[tab.key] > 0 ? 
                                                <span className="badge bg-secondary" 
                                                    style={{ fontSize: '10px', padding: '4px 6px', position: 'relative', top: '-1px' }}>{tabCounts[tab.key]}
                                                </span> : null}
                                        </span>
                                    </NavLink>
                                </NavItem>
                            ))}
                        </Nav>

                        <TabContent activeTab={activeTab}>
                            {tabs.map((tab) => (
                                <TabPane className="fade show" tabId={tab.key} key={tab.key}>
                                    {renderTable(filteredErrors)}
                                </TabPane>
                            ))}
                        </TabContent>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    );
};

export default DMTSyncErrors;
